import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { getRestaurantBySlug, getMenuItems, addOrder } from '../services/firebaseService';
import { trackAddToCart, trackPlaceOrder, trackPageView, trackEvent } from '../services/analyticsService';
import { getCurrencySymbol } from '../config/currencies';
import LoadingSpinner from '../components/loadingspinner';
import { FaCheckCircle } from 'react-icons/fa'; // Import success icon
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './MenuPage.css';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import LocationPicker from '../components/LocationPicker';

function OnlineOrderPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState(['All']);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [selectedPortions, setSelectedPortions] = useState({});
  const [phoneNumber, setPhoneNumber] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerLatitude, setCustomerLatitude] = useState(null);
  const [customerLongitude, setCustomerLongitude] = useState(null);
  const [countryCode, setCountryCode] = useState(null);
  const [dialCode, setDialCode] = useState('');
  const [step, setStep] = useState(1);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [area, setArea] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [landmark, setLandmark] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [addressType, setAddressType] = useState('Home');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [lastOrderDetails, setLastOrderDetails] = useState(null);
  const [waChecking, setWaChecking] = useState(false);
  const [waValidated, setWaValidated] = useState(false);
  const [waId, setWaId] = useState(null);
  const [waSkip, setWaSkip] = useState(false);
  const cartRef = useRef(null);
  const containerRef = useRef(null);
  const location = useLocation(); // Add this line
  const parseAddressParts = (addr) => {
    const parts = (addr || '').split(',').map(s => s.trim()).filter(Boolean);
    let line1 = parts[0] || '';
    let areaPart = parts[1] || '';
    let cityPart = parts[2] || parts[1] || '';
    const pinMatch = (addr || '').match(/\b\d{5,6}\b/);
    const pin = pinMatch ? pinMatch[0] : '';
    return { line1, areaPart, cityPart, pin };
  };
  const COUNTRY_DIAL = useMemo(() => ({
    US: '1', CA: '1', IN: '91', GB: '44', AU: '61', NZ: '64', SG: '65',
    AE: '971', SA: '966', QA: '974', KW: '965', OM: '968', BH: '973',
    LK: '94', MY: '60', JP: '81', KR: '82', CN: '86', HK: '852', TW: '886',
    TH: '66', PH: '63', ID: '62', VN: '84', PK: '92', BD: '880', NP: '977',
    RU: '7', BR: '55', MX: '52', AR: '54', CL: '56', CO: '57', PE: '51',
    ZA: '27', NG: '234', KE: '254', TZ: '255', UG: '256', ET: '251',
    DE: '49', FR: '33', ES: '34', IT: '39', NL: '31', BE: '32', SE: '46',
    NO: '47', FI: '358', CH: '41', GR: '30', PT: '351', IE: '353', PL: '48',
    CZ: '420', AT: '43', HU: '36', RO: '40', BG: '359', UA: '380', BY: '375',
    TR: '90', EG: '20', IL: '972', IQ: '964', IR: '98', JO: '962', PS: '970'
  }), []);
  useEffect(() => {
    if (countryCode) {
      const cc = String(countryCode).toUpperCase();
      setDialCode(COUNTRY_DIAL[cc] || '');
    }
  }, [countryCode, COUNTRY_DIAL]);

  // Removed useEffect for table_num from query params as it's not needed for online orders

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const restaurantData = await getRestaurantBySlug(slug);
        if (!restaurantData) {
          setError('Restaurant not found');
          setLoading(false);
          return;
        }
        setRestaurant(restaurantData);
        trackPageView(`OnlineOrder - ${restaurantData.name || slug}`, location.pathname + location.search, restaurantData.id);

        // Check if the restaurant is active
        if (restaurantData.isActive === false) { // Explicitly check for false
          setError('This restaurant is currently unavailable.');
          setLoading(false);
          return;
        }
        
        // Check if the restaurant is closed
        if (restaurantData.isClosed === true) {
          setError('This restaurant is currently closed.');
          setLoading(false);
          return;
        }

        // Check if online ordering is enabled
        if (restaurantData.onlineOrderingEnabled === false) { // Explicitly check if online ordering is disabled
          setError('Online ordering is currently disabled for this restaurant.');
          setLoading(false);
          return;
        }

        // First, check for active menus in the new menu structure
        let items = [];
        
        try {
          console.log(`OnlineOrderPage: Checking for active menus for restaurant ${restaurantData.id}`);
          const menusRef = collection(db, 'restaurants', restaurantData.id, 'menus');
          const activeMenuQuery = query(menusRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
          const activeMenuSnap = await getDocs(activeMenuQuery);
          
          console.log(`OnlineOrderPage: Found ${activeMenuSnap.size} active menus`);
          
          if (!activeMenuSnap.empty) {
            // If there are active menus, fetch items from all of them
            console.log(`OnlineOrderPage: Using ${activeMenuSnap.size} active menus`);
            
            for (const activeMenuDoc of activeMenuSnap.docs) {
              const activeMenu = activeMenuDoc;
              console.log(`OnlineOrderPage: Fetching items from menu: ${activeMenu.data().name} (ID: ${activeMenu.id})`);
              
              const menuItemsRef = collection(db, 'restaurants', restaurantData.id, 'menus', activeMenu.id, 'menuItems');
              const menuItemsSnap = await getDocs(menuItemsRef);
              
              const menuItems = menuItemsSnap.docs.map(doc => ({
                id: doc.id,
                menuId: activeMenu.id,
                menuName: activeMenu.data().name,
                ...doc.data()
              }));
              
              items = items.concat(menuItems);
              console.log(`OnlineOrderPage: Loaded ${menuItems.length} items from menu: ${activeMenu.data().name}`);
            }
            
            console.log(`OnlineOrderPage: Total loaded ${items.length} items from all active menus`);
          } else {
            // Fallback to legacy menu items if no active menu exists
            console.log(`OnlineOrderPage: No active menus found, falling back to legacy menu`);
            items = await getMenuItems(restaurantData.id);
            console.log(`OnlineOrderPage: Loaded ${items.length} items from legacy menu`);
          }
        } catch (menuError) {
          console.error('OnlineOrderPage: Error fetching active menus, falling back to legacy:', menuError);
          // If there's any error with the new menu structure, fall back to legacy
          try {
            items = await getMenuItems(restaurantData.id);
            console.log(`OnlineOrderPage: Fallback successful - loaded ${items.length} items from legacy menu`);
          } catch (legacyError) {
            console.error('OnlineOrderPage: Error fetching legacy menu items:', legacyError);
            throw legacyError; // Re-throw if even legacy fails
          }
        }

        // Include all items, don't filter out hidden ones
        setMenuItems(items);
        
        // Initialize selectedPortions for portion-wise items
        const initialPortions = {};
        items.forEach(item => {
          if (item.isPortionWise && item.portions && item.portions.length > 0) {
            initialPortions[item.id] = item.portions[0].name; // Default to first portion
          }
        });
        setSelectedPortions(initialPortions);
        const uniqueCategories = [...new Set(items.map(item => item.category))];
        setCategories(['All', ...uniqueCategories]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error); // Log the actual error
        setError('Failed to load menu data. ' + error.message);
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    if (cartRef.current && containerRef.current) {
      const cartHeight = cartRef.current.offsetHeight;
      // Add some extra padding, e.g., 20px, to ensure content is not cut off
      const newPaddingBottom = cartHeight > 0 ? cartHeight + 20 : 200; 
      containerRef.current.style.paddingBottom = `${newPaddingBottom}px`;
    }
  }, [cart, loading]); // Re-calculate when cart changes or after loading completes

  useEffect(() => {
    try {
      const saved = localStorage.getItem('cm_last_address');
      if (saved) {
        const v = JSON.parse(saved);
        setCustomerAddress(v.customerAddress || '');
        setCustomerLatitude(v.customerLatitude || null);
        setCustomerLongitude(v.customerLongitude || null);
        setAddressLine1(v.addressLine1 || '');
        setAddressLine2(v.addressLine2 || '');
        setArea(v.area || '');
        setCity(v.city || '');
        setPincode(v.pincode || '');
        setLandmark(v.landmark || '');
        setDeliveryInstructions(v.deliveryInstructions || '');
        setAddressType(v.addressType || 'Home');
      }
    } catch (_) {}
  }, []);

  const handleImageClick = (item) => {
    setModalItem(item);
    setShowImageModal(true);
  };
  const closeModal = () => {
    setShowImageModal(false);
    setModalItem(null);
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Helper function to handle portion selection change
  const handlePortionChange = (itemId, portion) => {
    setSelectedPortions(prev => ({
      ...prev,
      [itemId]: portion
    }));
  };

  // Helper function to get display price based on selected portion
  const getDisplayPrice = (item) => {
    if (!item.isPortionWise) {
      return item.price;
    }
    
    if (!item.portions || item.portions.length === 0) {
      return item.price; // Fallback to regular price if no portions
    }
    
    const selectedPortion = selectedPortions[item.id] || item.portions[0].name;
    const portion = item.portions.find(p => p.name === selectedPortion);
    return portion ? portion.price : item.portions[0].price;
  };

  const addToCart = (item) => {
    setCart(prev => {
      // For portion-wise items, create a unique identifier that includes the portion
      const selectedPortion = selectedPortions[item.id] || (item.portions && item.portions.length > 0 ? item.portions[0].name : null);
      const cartItemId = item.isPortionWise ? `${item.id}_${selectedPortion}` : item.id;
      
      const found = prev.find(ci => ci.cartItemId === cartItemId);
      if (found) {
        return prev.map(ci => ci.cartItemId === cartItemId ? { ...ci, qty: ci.qty + 1 } : ci);
      }
      
      // Create cart item with portion-specific pricing and name
      const cartItem = {
        ...item,
        cartItemId: cartItemId,
        qty: 1,
        price: item.isPortionWise ? getDisplayPrice(item) : (item.offerPrice && item.offerPrice < item.price ? item.offerPrice : item.price),
        originalPrice: item.isPortionWise ? getDisplayPrice(item) : item.price,
        name: item.isPortionWise ? `${item.name} (${selectedPortion})` : item.name,
        selectedPortion: item.isPortionWise ? selectedPortion : null
      };
      
      // New item added to cart
      trackAddToCart(cartItem, restaurant?.id); // Track add to cart event with restaurantId
      return [...prev, cartItem];
    });
  };

  const removeFromCart = (cartItemId) => {
    setCart(prev => prev.filter(ci => ci.cartItemId !== cartItemId));
  };

  const updateQty = (cartItemId, delta) => {
    setCart(prev => prev.map(ci => ci.cartItemId === cartItemId ? { ...ci, qty: Math.max(1, ci.qty + delta) } : ci));
  };

  const handleBook = async () => {
    if (!restaurant || cart.length === 0) return;
    if (!customerLatitude || !customerLongitude) {
      setError('Select location on map or use current location.');
      setStep(1);
      return;
    }
    if (!addressLine1 || !(area || city)) {
      setError('Fill required address fields.');
      setStep(2);
      return;
    }
    if (!phoneNumber || !dialCode) {
      setError('Enter WhatsApp number.');
      setStep(3);
      return;
    }
    setIsBooking(true);
    setError('');
    setSuccessMsg(''); // Clear previous messages
    try {
      const localDigits = (phoneNumber || '').replace(/\D/g, '');
      const e164 = dialCode ? `+${dialCode}${localDigits}` : localDigits;
      const orderPayload = {
        customerName: customerName, // Use state variable
        phoneNumber: e164, // Use E.164 phone
        customerAddress: customerAddress, // Use state variable
        customerLatitude: customerLatitude,
        customerLongitude: customerLongitude,
        customerLocation: { address: customerAddress, latitude: customerLatitude, longitude: customerLongitude },
        addressLine1,
        addressLine2,
        area,
        city,
        pincode,
        landmark,
        deliveryInstructions,
        addressType,
        countryCode,
        dialCode,
        items: cart.map(ci => ({
          menuItemId: ci.id, // Storing menuItemId for better tracking
          name: ci.name,
          qty: ci.qty,
          price: ci.price, // Use the price stored in cart item (already portion-wise)
          originalPrice: ci.originalPrice // Storing original price for reference
        })),
        totalAmount: cart.reduce((sum, ci) => sum + ci.price * ci.qty, 0),
        status: 'new', // Or 'received', 'pending_confirmation'
        createdAt: new Date(), // Add timestamp for the order
      };
      
      // Ensure restaurant.id is available
      if (!restaurant.id) {
        console.error("Restaurant ID is missing in handleBook, cannot place order.");
        setError('Error: Restaurant details are incomplete. Cannot place order.');
        setIsBooking(false);
        return;
      }

      // Pass restaurant.id as the first argument and the payload as the second
      // Assuming addOrder returns the order object with an ID or just the ID
      const newOrder = await addOrder(restaurant.id, orderPayload);

      // Track successful order placement
      trackPlaceOrder({
        restaurantId: restaurant.id,
        ...orderPayload // Spread the existing payload which includes totalAmount, items, customerName, etc.
      }); 
      
      // Store details for the success dialog
      setLastOrderDetails({
        ...orderPayload,
        id: newOrder.id || 'N/A', // Assuming newOrder has an id property or addOrder returns it
        orderNumber: newOrder.orderNumber // Ensure this uses the orderNumber field from the service response
      });

      setCart([]); // Clear the cart
      setShowSuccessDialog(true); // Show success dialog
      setSuccessMsg(''); // Clear any previous redirect message
      try {
        localStorage.setItem('cm_last_address', JSON.stringify({
          customerAddress,
          customerLatitude,
          customerLongitude,
          addressLine1,
          addressLine2,
          area,
          city,
          pincode,
          landmark,
          deliveryInstructions,
          addressType
        }));
      } catch (_) {}

      // No longer redirecting immediately
      // setTimeout(() => {
      //   // Navigate to the orders page for this restaurant
      //   navigate(`/orders/${restaurant.id}`); 
      // }, 2000); // Increased timeout for better UX

    } catch (err) {
      console.error('Failed to place order:', err);
      setError(`Failed to place order. ${err.message ? err.message : 'Please try again.'}`);
    } finally {
      setIsBooking(false);
    }
  };

  const currencySymbol = (restaurant && restaurant.currency && getCurrencySymbol(restaurant.currency)) || '$';

  const handleOrderMore = () => {
    setShowSuccessDialog(false);
    setLastOrderDetails(null);
    // Optionally reset customer name and table number or show info dialog again
    // setCustomerName('');
    // setPhoneNumber('');
    // setCustomerAddress('');
    setShowInfoDialog(true); // Take user back to enter details for a new order
  };

const handleDownloadReceipt = () => {
    if (!lastOrderDetails) return;

    const doc = new jsPDF();
    const currencyCode = restaurant?.currency || 'USD'; // Use currency code, fallback to USD

    // Restaurant Name
    doc.setFontSize(18);
    doc.text(restaurant?.name || 'Restaurant', 105, 20, { align: 'center' });

    // Title
    doc.setFontSize(14);
    doc.text('Order Confirmation', 105, 30, { align: 'center' });

    // Order Details
    doc.setFontSize(10);
    doc.text(`Order Number: ${lastOrderDetails.orderNumber}`, 14, 45);
    doc.text(`Customer Name: ${lastOrderDetails.customerName}`, 14, 52);
    doc.text(`Phone Number: ${lastOrderDetails.phoneNumber}`, 14, 59);
    doc.text(`Address: ${lastOrderDetails.customerAddress}`, 14, 66);
    doc.text(`Date: ${new Date(lastOrderDetails.createdAt).toLocaleString()}`, 14, 73);

    // Items Table
    const tableColumn = ["Item Name", "Quantity", "Price", "Total"];
    const tableRows = [];

    lastOrderDetails.items.forEach(item => {
      const itemData = [
        item.name,
        item.qty,
        `${item.price.toFixed(2)} ${currencyCode}`,
        `${(item.price * item.qty).toFixed(2)} ${currencyCode}`
      ];
      tableRows.push(itemData);
    });

    autoTable(doc, {
      startY: 80,
      head: [tableColumn],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [22, 160, 133] }, // Green header
      margin: { top: 10 }
    });

    // Total Amount
    let finalY = doc.lastAutoTable.finalY || 80; // Get Y position after table
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total Amount: ${lastOrderDetails.totalAmount.toFixed(2)} ${currencyCode}`, 14, finalY + 15);

    // Thank you message
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    doc.text('Thank you for your order!', 105, finalY + 25, { align: 'center' });

    // Save PDF
    doc.save(`order_receipt_${lastOrderDetails.orderNumber}.pdf`);
  };

  const ImageModal = ({ item, onClose }) => {
    if (!item) return null;
  
    return (
      <div className="image-modal-overlay" onClick={onClose}>
        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}> 
          {item.photoUrl ? (
            <img src={item.photoUrl} alt={item.name} />
          ) : (
            <div className="modal-image-placeholder">
              <i className="fas fa-utensils"></i>
              <span>No Image Available</span>
            </div>
          )}
          <div className="image-modal-info">
            <h3 className="modal-item-name">{item.name}</h3>
            {item.description && (
              <p className="modal-item-description">{item.description}</p>
            )}
            <div className="modal-item-price">
              {item.offerPrice && item.offerPrice < item.price ? (
                <>
                  <span className="modal-offer-price">
                    {getCurrencySymbol(restaurant?.currency)}{item.offerPrice}
                  </span>
                  <span className="modal-original-price">
                    {getCurrencySymbol(restaurant?.currency)}{item.price}
                  </span>
                </>
              ) : (
                <span className="modal-regular-price">
                  {getCurrencySymbol(restaurant?.currency)}{item.price}
                </span>
              )}
            </div>
            {item.prepTime && (
              <div className="modal-prep-time">
                <i className="far fa-clock"></i>
                {item.prepTime} min
              </div>
            )}
          </div>
          <button className="image-modal-close" onClick={onClose}>&times;</button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  if (error) {
    // Check if it's a restaurant closure message
    const isClosedMessage = error.includes('closed');
    
    return (
      <div className="container centered">
        <div className={`error-container ${isClosedMessage ? 'restaurant-closed' : ''}`}>
          {isClosedMessage ? (
            <>
              <div className="closed-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="64" height="64" fill="#e74c3c">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>
                </svg>
              </div>
              <h2 className="closed-title">This Restaurant is Currently Closed</h2>
              <p className="closed-message">We're sorry, but this restaurant is not accepting online orders at this time.</p>
              <p className="closed-hours">Please check back later during business hours.</p>
            </>
          ) : (
            <p className="error-message">{error}</p>
          )}
          <Link to="/" className="btn mt-4">Go Home</Link>
        </div>
      </div>
    );
  }

  const validatePhone = (v, hasDial) => {
    const digits = (v || '').replace(/\D/g, '');
    return !!hasDial && digits.length >= 6;
  };

  const canStep1 = !!(customerLatitude && customerLongitude);
  const canStep2 = !!(addressLine1 && (area || city));
  const canStep3 = !!(waValidated || waSkip);
  const nextFrom1 = () => { if (canStep1) { setStep(2); setError(''); trackEvent('step_location_complete', { restaurant_id: restaurant?.id }); } else { setError('Select location.'); } };
  const nextFrom2 = () => { if (canStep2) { setStep(3); setError(''); trackEvent('step_address_complete', { restaurant_id: restaurant?.id, address_type: addressType }); } else { setError('Complete address.'); } };
  const nextFrom3 = () => { if (canStep3) { setStep(4); setError(''); trackEvent('step_phone_complete', { restaurant_id: restaurant?.id }); } else { setError('Enter valid WhatsApp number or skip verification.'); } };
  const prevFrom2 = () => { setStep(1); setError(''); };
  const prevFrom3 = () => { setStep(2); setError(''); };

  const checkWhatsApp = async () => {
    const localDigits = (phoneNumber || '').replace(/\D/g, '');
    const e164 = dialCode ? `+${dialCode}${localDigits}` : '';
    if (!validatePhone(phoneNumber, !!dialCode)) {
      setError('Enter valid WhatsApp number.');
      setWaValidated(false);
      return;
    }
    try {
      setWaChecking(true);
      setError('');
      setWaValidated(false);
      setWaId(null);
      const resp = await fetch('https://us-central1-checkmenus.cloudfunctions.net/checkWhatsAppContact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ e164 })
      });
      const data = await resp.json();
      if (resp.ok && data && data.valid) {
        setWaValidated(true);
        setWaId(data.wa_id || null);
      } else if (resp.status === 501) {
        setWaValidated(false);
        setWaId(null);
        setError('WhatsApp validation not configured. Please contact support.');
      } else {
        setWaValidated(false);
        setWaId(null);
        setError('This number is not on WhatsApp.');
      }
    } catch (e) {
      setWaValidated(false);
      setWaId(null);
      setError('WhatsApp validation failed. Try again later.');
    } finally {
      setWaChecking(false);
    }
  };

  if (step !== 4 && !showSuccessDialog) {
    return (
      <div className="container centered" style={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', width: '100%', maxWidth: '480px' }}>
          <div style={{ marginBottom: '16px', fontWeight: 700, textAlign: 'center' }}>Step {step} of 3</div>
          {error && <p style={{ color: 'red', textAlign: 'center', marginBottom: '15px' }}>{error}</p>}
          {step === 1 && (
            <>
          <div style={{ marginBottom: '20px' }}>
            <LocationPicker
              value={{ address: customerAddress, latitude: customerLatitude, longitude: customerLongitude }}
              onChange={(v) => { 
                setCustomerAddress(v.address); 
                setCustomerLatitude(v.latitude); 
                setCustomerLongitude(v.longitude); 
                if (v.countryCode) setCountryCode(v.countryCode); 
                if (v.components) {
                  setAddressLine1(v.components.street || '');
                  setArea(v.components.area || '');
                  setCity(v.components.city || '');
                  setPincode(v.components.postcode || '');
                } else {
                  const p = parseAddressParts(v.address); 
                  setAddressLine1(prev => prev || p.line1); 
                  setArea(prev => prev || p.areaPart); 
                  setCity(prev => prev || p.cityPart); 
                  setPincode(prev => prev || p.pin); 
                }
              }}
              required
            />
          </div>
              <button type="button" className="btn btn-primary" onClick={nextFrom1} style={{ width: '100%', padding: '12px', fontSize: '16px' }} disabled={!canStep1}>Continue</button>
            </>
          )}
          {step === 2 && (
            <>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                {['Home','Work','Other'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setAddressType(t); trackEvent('address_type_select', { restaurant_id: restaurant?.id, type: t }); }}
                    className="btn"
                    style={{
                      flex: 1,
                      backgroundColor: addressType === t ? '#2563eb' : '#eee',
                      color: addressType === t ? '#fff' : '#333',
                      border: 'none',
                      padding: '10px',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Address Line 1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Address Line 2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
                <input
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Pincode"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
                <input
                  type="text"
                  placeholder="Landmark"
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <textarea
                  rows={2}
                  placeholder="Delivery Instructions"
                  value={deliveryInstructions}
                  onChange={(e) => setDeliveryInstructions(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ marginBottom: '10px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 6, padding: '10px' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Address Summary</div>
                <div style={{ fontSize: '0.95rem', color: '#333' }}>
                  {(addressLine1 || '').trim()} {(addressLine2 || '').trim()} {(area || '').trim()} {(city || '').trim()} {(pincode || '').trim()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={prevFrom2} style={{ flex: 1, padding: '12px', fontSize: '16px' }}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={nextFrom2} style={{ flex: 1, padding: '12px', fontSize: '16px' }} disabled={!canStep2}>
                  Continue
                </button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value={dialCode ? `+${dialCode}` : ''}
                    placeholder="Country code"
                    style={{ width: '120px', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', backgroundColor: '#f8f9fa' }}
                  />
                  <input 
                    type="tel" 
                    placeholder="WhatsApp Number"
                    value={phoneNumber} 
                    onChange={(e) => { setPhoneNumber(e.target.value); setWaValidated(false); setWaId(null); setWaSkip(false); }} 
                    style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <button type="button" className="btn btn-secondary" onClick={checkWhatsApp} disabled={waChecking} style={{ padding: '10px 12px' }}>
                  {waChecking ? 'Checking...' : 'Verify WhatsApp'}
                </button>
                {waValidated && <span style={{ color: '#16a34a', fontWeight: 600 }}>Verified</span>}
              </div>
              <div style={{ marginTop: '6px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={waSkip}
                    onChange={(e) => { setWaSkip(e.target.checked); setError(''); trackEvent('wa_skip_toggle', { restaurant_id: restaurant?.id, checked: e.target.checked }); }}
                  />
                  Continue without WhatsApp verification
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={prevFrom3} style={{ flex: 1, padding: '12px', fontSize: '16px' }}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={nextFrom3} style={{ flex: 1, padding: '12px', fontSize: '16px' }} disabled={!canStep3}>
                  Continue
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (showSuccessDialog && lastOrderDetails) {
    return (
      <div className="container centered" style={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', width: '100%', maxWidth: '500px', textAlign: 'center' }}>
          <FaCheckCircle size={50} color="green" style={{ marginBottom: '20px' }} />
          <h2 style={{ marginBottom: '20px' }}>Order Placed Successfully!</h2>
          
          <div style={{ textAlign: 'left', marginBottom: '20px' }}>
            <p><strong>Order Number:</strong> {lastOrderDetails.orderNumber}</p>
            <p><strong>Name:</strong> {lastOrderDetails.customerName}</p>
            <p><strong>Phone:</strong> {lastOrderDetails.phoneNumber}</p>
            <p><strong>Address:</strong> {lastOrderDetails.customerAddress}</p>
            <p><strong>Address Type:</strong> {lastOrderDetails.addressType}</p>
            {lastOrderDetails.landmark && (<p><strong>Landmark:</strong> {lastOrderDetails.landmark}</p>)}
            {lastOrderDetails.deliveryInstructions && (<p><strong>Instructions:</strong> {lastOrderDetails.deliveryInstructions}</p>)}
            {lastOrderDetails.customerLatitude && lastOrderDetails.customerLongitude && (
              <p><strong>Location:</strong> {lastOrderDetails.customerLatitude.toFixed(6)}, {lastOrderDetails.customerLongitude.toFixed(6)} {' '}<a href={`https://www.google.com/maps?q=${lastOrderDetails.customerLatitude},${lastOrderDetails.customerLongitude}`} target="_blank" rel="noreferrer">View on Map</a></p>
            )}
            <h4 style={{ marginTop: '15px', marginBottom: '5px' }}>Items:</h4>
            <ul style={{ listStyle: 'none', paddingLeft: '0', maxHeight: '150px', overflowY: 'auto' }}>
              {lastOrderDetails.items.map((item, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>
                  {item.name} (x{item.qty}) - {currencySymbol}{item.price.toFixed(2)}
                </li>
              ))}
            </ul>
            <p style={{ marginTop: '10px', fontWeight: 'bold' }}><strong>Total:</strong> {currencySymbol}{lastOrderDetails.totalAmount.toFixed(2)}</p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '30px' }}>
            <button onClick={handleOrderMore} className="btn btn-secondary" style={{ margin: '10px' }}>Order More</button>
            <button onClick={handleDownloadReceipt} className="btn btn-primary" style={{ margin: '10px' }}>Download Receipt</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" ref={containerRef} style={{ paddingBottom: '200px' }}>
      <div className="header" style={{ position: 'relative', width: '100%' }}>
        <div className="header-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          {restaurant?.logoUrl && (
            <div style={{ width: '100%' }}>
              <img src={restaurant.logoUrl} alt={`${restaurant.name} Logo`} style={{ width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover' }} />
            </div>
          )}
          <h1 style={{ textAlign: 'center', margin: '0', fontSize: '2rem', fontWeight: 'bold', width: '100%' }}>{restaurant?.name}</h1>
        </div>
      </div>
      <div className="search-bar">
        <i className="fas fa-search"></i>
        <input type="text" placeholder="Search menu items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
      </div>
      <div className="category-tabs">
        {categories.map(category => (
          <div key={category} className={`category-tab ${activeCategory === category ? 'active' : ''}`} onClick={() => setActiveCategory(category)}>{category}</div>
        ))}
      </div>
      {filteredItems.length === 0 ? (
        <div className="centered mt-4"><p>No menu items found.</p></div>
      ) : (
        <div className="menu-grid">
          {filteredItems.map(item => (
            <div key={item.id} className={`menu-item ${item.isHidden ? 'menu-item-disabled' : ''}`}>
              {item.isHidden && (
                <div className="out-of-stock-overlay">
                  <span>Out of Stock</span>
                </div>
              )}
              {item.photoUrl ? (
                <div className="menu-item-image-container" onClick={!item.isHidden ? () => handleImageClick(item) : undefined} style={{ cursor: item.isHidden ? 'default' : 'pointer' }}>
                  <img src={item.photoUrl} alt={item.name} />
                </div>
              ) : (
                <div className="menu-item-placeholder"></div>
              )}
              {item.offerPrice && item.offerPrice < item.price && !item.isHidden && (<div className="offer-badge">Offer</div>)}
              <div className="menu-item-content" style={{ marginBottom: 10 }}>
                <div className="menu-item-category">
                  <h3 className="menu-item-title">{item.name}</h3>
                </div>
                {item.description && (<p className="menu-item-description">{item.description}</p>)}
                
                {/* Portion Selection Radio Buttons - Only for portion-wise items */}
                {item.isPortionWise && item.portions && item.portions.length > 0 && (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      gap: '15px', 
                      marginBottom: '10px', 
                      justifyContent: 'left', 
                      flexWrap: 'wrap' 
                    }}> 
                      {item.portions.map(portion => ( 
                        <label key={portion.name} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer', 
                          fontSize: '0.9rem', 
                          fontWeight: selectedPortions[item.id] === portion.name ? 'bold' : 'normal', 
                          color: selectedPortions[item.id] === portion.name ? '#2563eb' : '#666'
                        }}> 
                          <input 
                            type="radio" 
                            name={`portion-${item.id}`} 
                            value={portion.name} 
                            checked={selectedPortions[item.id] === portion.name} 
                            onChange={() => handlePortionChange(item.id, portion.name)} 
                            style={{ marginRight: '5px' }} 
                          /> 
                          {portion.name}
                        </label> 
                      ))} 
                    </div>
                    
                    {/* Show description only for selected portion */}
                    {(() => {
                      const selectedPortion = selectedPortions[item.id] || item.portions[0].name;
                      const portion = item.portions.find(p => p.name === selectedPortion);
                      return portion && portion.description ? (
                        <div style={{ 
                          fontSize: '0.85rem', 
                          color: '#666', 
                          marginBottom: '10px',
                          lineHeight: '1.3',
                          fontStyle: 'italic',
                          padding: '8px 12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          {portion.description}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
                
                <div className="menu-item-footer">
                  <div className="menu-item-price">
                    {item.offerPrice && item.offerPrice < item.price && !item.isPortionWise ? (
                      <>
                        {/* Offer price - displayed prominently */}
                        <span style={{ 
                          fontSize: '1rem',  // Make offer price slightly larger
                          fontWeight: 'bold',  // Make offer price bold
                          color: '#2563eb'     // Optional: make offer price blue to highlight savings
                        }}>
                          {getCurrencySymbol(restaurant?.currency)}{item.offerPrice}
                        </span>

                        {/* Original price - displayed smaller with strikethrough */}
                        <span style={{ 
                          textDecoration: 'line-through', 
                          color: '#888', 
                          marginLeft: '8px',  // Changed to marginLeft for better spacing
                          fontSize: '0.8rem'  // Make original price smaller
                        }}>
                          {getCurrencySymbol(restaurant?.currency)}{" "}{item.price}
                        </span>
                      </>
                    ) : (
                      // If no offer price or portion-wise item, show appropriate price
                      <span style={{ fontWeight: 'bold' }}>
                        {getCurrencySymbol(restaurant?.currency)}{" "}{getDisplayPrice(item)}
                      </span>
                    )}
                  </div>
                  <button className="btn add-btn" onClick={() => addToCart(item)} disabled={item.isHidden} style={{ marginLeft: 10, opacity: item.isHidden ? 0.5 : 1, cursor: item.isHidden ? 'not-allowed' : 'pointer' }}>Add</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="cart-section" ref={cartRef} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, width: '100%', background: '#f9f9f9', padding: '20px', borderRadius: '8px 8px 0 0', zIndex: 1000, boxShadow: '0 -2px 10px rgba(0,0,0,0.1)' }}>
        <h2>Cart</h2>
        {cart.length === 0 ? (
          <p>Your cart is empty.</p>
        ) : (
          <div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {cart.map(ci => (
                <div key={ci.cartItemId} className="cart-item" style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ flex: 1, whiteSpace: 'break-spaces', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ci.name}</span>
                  <span style={{ margin: '0 10px' }}>{currencySymbol}{ci.price}</span>
                  <button onClick={() => updateQty(ci.cartItemId, -1)} disabled={ci.qty === 1} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', fontSize: '1.5em', color: '#007bff' }}>
                    <i className="fas fa-minus-circle"></i>
                  </button>
                  <span style={{ margin: '0 8px', verticalAlign: 'middle', fontSize: '1.1em' }}>{ci.qty}</span>
                  <button onClick={() => updateQty(ci.cartItemId, 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', fontSize: '1.5em', color: '#28a745' }}>
                    <i className="fas fa-plus-circle"></i>
                  </button>
                  <button onClick={() => removeFromCart(ci.cartItemId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'red', marginLeft: '10px', padding: '5px', fontSize: '1.5em' }}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontWeight: 'bold' }}>Total: {currencySymbol}{cart.reduce((sum, ci) => sum + ci.price * ci.qty, 0).toFixed(2)}</div>
            <button className="btn btn-primary" onClick={handleBook} disabled={isBooking} style={{ marginTop: 15 }}>Confirm Order</button>
            {successMsg && <div className="success-message" style={{ color: 'green', marginTop: 10 }}>{successMsg}</div>} {/* This might not be needed anymore or could show other success messages */}
            {error && !successMsg && <div className="error-message" style={{ color: 'red', marginTop: 10 }}>{error}</div>} {/* Display error from handleBook if any */}
          </div>
        )}
      </div>
      <ImageModal item={modalItem} onClose={closeModal} />
    </div>
  );
}

export default OnlineOrderPage;
